import { normalizeAnswers } from "../../../pearliCore/assessment";
import React, { useState, useEffect } from "react";
import "./Card.css";
import ArrowCircleRight from "../../../assets/ArrowCircleRight.svg";
import { useCookies } from "react-cookie";
import axios from "axios";
import { headerConfig } from "../../../axiosconfig.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const Card = ({
  domain,
  completedTabs,
  activeTab: initialActiveTab,
  onComplete,
  onDomainProgressUpdate,
}) => {
  const { state } = useLocation();
  const [progress, setProgress] = useState(0);
  const [answers, setAnswers] = useState(Array(5).fill(undefined));
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(domain);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [tones, setQuestionTones] = useState([]);
  const [questionnumber, setQuestionnumber] = useState([]);
  const [cookies] = useCookies(["key"]); // ✅ use correct cookie name
  const [isAnswerSelected, setIsAnswerSelected] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(Array(5).fill(false));
  const [currentAnswer, setCurrentAnswer] = useState(undefined);
  const [domainQuestionCount, setDomainQuestionCount] = useState(0);
  const [domainAnsweredCount, setDomainAnsweredCount] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const nextDomain = state?.nextDomain;

  const domainFormatted =
    activeTab.charAt(0).toUpperCase() + activeTab.slice(1);

  const fetchQuestions = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/selfAssesments`,
        {
          request: "getQuestions",
          key: cookies["key"],
          domain: domainFormatted,
        },
        headerConfig
      );
      const data = response.data;

      // ✅ Compatible with new BE: result: 1, status: "success"
      if (data.result === 1 || data.status === "success") {
        let Q = ["", "", "", "", ""];
        let tone = ["", "", "", "", ""];
        let Qn = ["", "", "", "", ""];
        for (let a = 0; a < data.questions.length; a++) {
          Q[a] = data.questions[a][0];
          tone[a] = data.questions[a][1] === "Negative";
          Qn[a] = data.questions[a][2];
        }

        setQuestions(Q);
        setQuestionTones(tone);
        setQuestionnumber(Qn);
        setDomainQuestionCount(data.questions.length);
        setDomainAnsweredCount(
          answers.filter((answer) => answer !== undefined).length
        );

        if (onDomainProgressUpdate) {
          onDomainProgressUpdate(
            activeTab,
            (answers.filter((answer) => answer !== undefined).length /
              data.questions.length) *
              100
          );
        }
      } else {
        console.error("Failed to fetch questions:", data);
        setCurrentQuestion("Failed to load question.");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      setCurrentQuestion("Error loading question.");
    }
  };

  const handleStartReflection = () => {
    let answerToSave = currentAnswer;
    if (answerToSave === undefined && answers[questionIndex] !== undefined) {
      answerToSave = answers[questionIndex];
    }

    if (answerToSave === undefined) {
      toast.warn("Please select an answer before proceeding.");
      return;
    }

    if (isSaving) return;

    const updatedAnswers = [...answers];
    updatedAnswers[questionIndex] = answerToSave;
    setAnswers(updatedAnswers);
    setHasAnswered((prev) => {
      const nextHasAnswered = [...prev];
      nextHasAnswered[questionIndex] = true;
      return nextHasAnswered;
    });

    const answeredQuestions = updatedAnswers.filter(
      (answer) => answer !== undefined
    ).length;
    setDomainAnsweredCount(answeredQuestions);

    if (onDomainProgressUpdate) {
      const progressValue = (answeredQuestions / domainQuestionCount) * 100;
      onDomainProgressUpdate(activeTab, progressValue);
    }

    if (questionIndex < questions.length - 1) {
      setQuestionIndex(questionIndex + 1);
      setProgress(
        answers[questionIndex + 1] !== undefined
          ? answers[questionIndex + 1]
          : 0
      );
      setCurrentAnswer(answers[questionIndex + 1]);
      setIsAnswerSelected(answers[questionIndex + 1] !== undefined);
    } else {
      if (
        updatedAnswers.filter((answer) => answer !== undefined).length === 5
      ) {
        setCurrentQuestion(
          `You have successfully completed ${domain} Assessment!`
        );
        saveScores(updatedAnswers).then((saved) => {
          if (!saved) return;
          onComplete(domainFormatted);

          if (nextDomain) {
            navigate(`/Assessment/maincontent/${nextDomain}`);
          } else {
            navigate("/UserData");
          }
        });
      } else {
        toast.error("Please answer all the questions in this section.");
        console.error(
          "Not all questions answered before attempting to complete domain."
        );
      }
    }
  };

  const handleProgressBarClick = (e) => {
    const barWidth = e.currentTarget.offsetWidth;
    const clickPosition =
      e.clientX - e.currentTarget.getBoundingClientRect().left;
    const rawProgress = (clickPosition / barWidth) * 5;
    const newProgress =
      Math.max(1, Math.min(5, Math.ceil(rawProgress)));

    if (!isSaving) {
      setProgress(newProgress);
      setCurrentAnswer(newProgress);
      setIsAnswerSelected(true);
      const updatedAnswers = [...answers];
      updatedAnswers[questionIndex] = newProgress;
      setAnswers(updatedAnswers);
    }
    // optional: you can remove the old auto-save block here
  };

  const handleAnswerSelect = (selectedScore) => {
    if (!isSaving) {
      setProgress(selectedScore);
      setCurrentAnswer(selectedScore);
      setIsAnswerSelected(true);
      const updatedAnswers = [...answers];
      updatedAnswers[questionIndex] = selectedScore;
      setAnswers(updatedAnswers);
    }
  };

  const notifySuccess = () => toast.success("Scores saved successfully!");
  const notifyError = () => toast.error("Submission failed. Please try again.");

  const saveScores = async (scores) => {
    if (scores.length === 5) {
      try {
        setIsSaving(true);
        const dataToSend = {
          request: "saveScore",
          domain: domainFormatted, // ✅ full domain name
          score: normalizeAnswers(scores, tones.map(negative => negative ? "Negative" : "Positive")),
          rawScores: scores,
          questions: questionnumber,
          key: cookies["key"],
        };

        const response = await axios.post(
          `${API_URL}/selfAssesments`,
          dataToSend
        );
        console.log(response.data);

        const { result, status, persisted } = response.data;

        // ✅ compatible with new BE: 1 or 2 = success
        if (result === 2 || result === 1) {
          notifySuccess();
          console.log("Save status:", status, "persisted:", persisted);
          setAnswers([]);
          return true;
        } else {
          toast.error("Error saving scores");
        }
      } catch (error) {
        console.error("Error saving scores:", error);
        notifyError();
      } finally {
        setIsSaving(false);
      }
    }
    return false;
  };

  useEffect(() => {
    fetchQuestions();
  }, [activeTab]);

  useEffect(() => {
    if (questionIndex >= 0) {
      localStorage.setItem("lastQuestionIndex", questionIndex);
    }
  }, [questionIndex]);

  useEffect(() => {
    if (questions.length > 0) {
      setCurrentQuestion(questions[questionIndex] || "");
      setProgress(
        answers[questionIndex] !== undefined ? answers[questionIndex] : 0
      );
      setCurrentAnswer(answers[questionIndex]);
      setIsAnswerSelected(answers[questionIndex] !== undefined);
    } else {
      setCurrentQuestion("No questions available.");
    }
  }, [questionIndex, questions, answers]);

  const handleRevert = () => {
    if (questionIndex > 0) {
      const previousIndex = questionIndex - 1;
      setQuestionIndex(previousIndex);
    }
  };

  return (
    <div className="card-tab">
      <div className="Card">
        <p className="text-card">Reflecting on Your Well-Being 🌱</p>
        <div className="emoji-progrss">
          <div className="emoji-progress-defult">
            <div className="container-emoji">
              <div className="statement-container">
                <div className="frame-70">
                  <span className="text-name">
                    {`Question ${Math.min(
                      questionIndex + 1,
                      questions.length
                    )} of ${questions.length}`}
                  </span>
                  <div className="bedge-emoji">
                    <span>How accurate is this statement?</span>
                  </div>
                </div>
                <p className="emoji-text">{currentQuestion}</p>
              </div>

              <div className="emoji-progrss-bar">
                <div className="bar-container">
                  <div className="bar" onClick={handleProgressBarClick}>
                    <div className="vector1">
                      <div className="bar-background" />
                      <div
                        className="bar-fill"
                        style={{ width: `${(progress / 5) * 100}%` }}
                      />
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="vertical-line"
                          style={{ left: `${(i / 5) * 100}%` }}
                          onClick={() => handleAnswerSelect(i + 1)}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="frame-75">
                    {[
                      "Totally Disagree",
                      "Disagree",
                      "Neutral",
                      "Agree",
                      "Totally Agree",
                    ].map((text, i) => (
                      <p
                        key={i}
                        className={`text-frame75 ${
                          progress === i + 1 ? "selected-text" : ""
                        }`}
                        onClick={() => handleAnswerSelect(i + 1)}
                      >
                        {text}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="alert-emoji">
            <div className="stack-emoji">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M9.99984 18.3337C14.6022 18.3337 18.3332 14.6027 18.3332 10.0003C18.3332 5.39795 14.6022 1.66699 9.99984 1.66699C5.39746 1.66699 1.6665 5.39795 1.6665 10.0003C1.6665 14.6027 5.39746 18.3337 9.99984 18.3337Z"
                  stroke="#1F2937"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 13.3333V10"
                  stroke="#1F2937"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 6.66699H10.0083"
                  stroke="#1F2937"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="Stack-emoji-text">
                <p className="stack-text">
                  Research shows honest self-reflection boosts well-being.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card-button">
          <div className="card-btn-group">
            <button className="card-btn1" onClick={handleRevert}>
              <p className="btn1-text ">Revisit</p>
              <div className="revist-icon">{/* ...icon svg... */}</div>
            </button>
            <button
              className="card-btn2"
              onClick={handleStartReflection}
              disabled={!isAnswerSelected || isSaving}
            >
              <span className="btn2-text">Reflect Further</span>
              <img className="arrow" src={ArrowCircleRight} alt="Arrow" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
